import { useState, useEffect, useCallback } from 'react';

export interface CartItem {
  productId: string;
  merchantId: string;
  name: string;
  price: number;
  currency: string;
  quantity: number;
  image?: string;
  category?: string; // From AI extraction
  description?: string; // From menu/catalog
}

interface ShoppingCart {
  merchantId: string;
  merchantName?: string;
  currency: string;
  items: CartItem[];
  updatedAt: string;
}

const CART_STORAGE_KEY = 'vaiu_shopping_cart';

export const useShoppingCart = (merchantId?: string) => {
  const [cart, setCart] = useState<ShoppingCart | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load cart from sessionStorage on mount
  useEffect(() => {
    const loadCart = () => {
      try {
        const stored = sessionStorage.getItem(CART_STORAGE_KEY);
        if (stored) {
          const parsedCart = JSON.parse(stored) as ShoppingCart;
          
          // If merchantId is provided, only load cart for that merchant
          if (merchantId && parsedCart.merchantId !== merchantId) {
            // Initialize new cart for this merchant
            setCart({
              merchantId,
              currency: 'USD',
              items: [],
              updatedAt: new Date().toISOString()
            });
          } else {
            setCart(parsedCart);
          }
        } else if (merchantId) {
          // Initialize new cart if none exists
          setCart({
            merchantId,
            currency: 'USD',
            items: [],
            updatedAt: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Failed to load cart from storage:', error);
        if (merchantId) {
          setCart({
            merchantId,
            currency: 'USD',
            items: [],
            updatedAt: new Date().toISOString()
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadCart();
  }, [merchantId]);

  // Save cart to sessionStorage whenever it changes
  useEffect(() => {
    if (!isLoading && cart) {
      try {
        sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
      } catch (error) {
        console.error('Failed to save cart to storage:', error);
      }
    }
  }, [cart, isLoading]);

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>, quantity: number = 1) => {
    setCart(currentCart => {
      if (!currentCart) {
        // Initialize cart if it doesn't exist
        return {
          merchantId: item.merchantId,
          currency: item.currency,
          items: [{
            ...item,
            quantity
          }],
          updatedAt: new Date().toISOString()
        };
      }

      // Check if merchant matches
      if (currentCart.merchantId !== item.merchantId) {
        // Different merchant - ask user to clear cart or cancel
        const confirmSwitch = window.confirm(
          'You have items from a different merchant in your cart. Clear cart and add this item?'
        );
        
        if (confirmSwitch) {
          return {
            merchantId: item.merchantId,
            currency: item.currency,
            items: [{
              ...item,
              quantity
            }],
            updatedAt: new Date().toISOString()
          };
        }
        return currentCart;
      }

      // Check if item already exists
      const existingItemIndex = currentCart.items.findIndex(
        i => i.productId === item.productId
      );

      if (existingItemIndex >= 0) {
        // Update quantity
        const updatedItems = [...currentCart.items];
        updatedItems[existingItemIndex].quantity += quantity;
        
        return {
          ...currentCart,
          items: updatedItems,
          updatedAt: new Date().toISOString()
        };
      } else {
        // Add new item
        return {
          ...currentCart,
          items: [...currentCart.items, { ...item, quantity }],
          updatedAt: new Date().toISOString()
        };
      }
    });
  }, []);

  const removeItem = useCallback((productId: string) => {
    setCart(currentCart => {
      if (!currentCart) return null;
      
      const updatedItems = currentCart.items.filter(
        item => item.productId !== productId
      );
      
      // If no items left, clear the cart
      if (updatedItems.length === 0) {
        return null;
      }
      
      return {
        ...currentCart,
        items: updatedItems,
        updatedAt: new Date().toISOString()
      };
    });
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setCart(currentCart => {
      if (!currentCart) return null;
      
      const updatedItems = currentCart.items.map(item =>
        item.productId === productId
          ? { ...item, quantity }
          : item
      );
      
      return {
        ...currentCart,
        items: updatedItems,
        updatedAt: new Date().toISOString()
      };
    });
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setCart(null);
    try {
      sessionStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear cart from storage:', error);
    }
  }, []);

  const getTotalAmount = useCallback(() => {
    if (!cart || cart.items.length === 0) return 0;
    
    return cart.items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  }, [cart]);

  const getTotalItems = useCallback(() => {
    if (!cart || cart.items.length === 0) return 0;
    
    return cart.items.reduce((total, item) => {
      return total + item.quantity;
    }, 0);
  }, [cart]);

  const getCartForMerchant = useCallback((targetMerchantId: string) => {
    if (!cart || cart.merchantId !== targetMerchantId) return [];
    return cart.items;
  }, [cart]);

  const setMerchantName = useCallback((name: string) => {
    setCart(currentCart => {
      if (!currentCart) return null;
      
      return {
        ...currentCart,
        merchantName: name,
        updatedAt: new Date().toISOString()
      };
    });
  }, []);

  return {
    cart,
    items: cart?.items || [],
    isLoading,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getTotalAmount,
    getTotalItems,
    getCartForMerchant,
    setMerchantName,
    merchantId: cart?.merchantId,
    currency: cart?.currency || 'USD'
  };
};
